import React, { useCallback, useState, useContext, useMemo, useEffect } from "react";
import { Box, Checkbox, List, ListItemButton, ListItemText, Tooltip, Typography } from "@mui/material";
import { IFailureDetailsSettingsList } from "./FailureDetailsSettingsListInterfaces";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import { useStyles } from "./FailureDetailsSettingsList.styles";
import { IFailureCode } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { AddFailureTypesModal } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/AddFailureTypesModal/AddFailureTypesModal.view";
import { useTranslation } from "react-i18next";
import { FailureTypeCreationForm } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/forms/FailureTypeCreationForm/FailureTypeCreationForm.view";
import { AddFailureCodesModal } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/AddFailureCodesModal/AddFailureCodesModal.view";
import { FailureCodeCreationForm } from "pages/Corporate/CorporateSetupDashboard/MachineCenterDetails/FailureDetailsSettings/forms/FailureCodeForm/FailureCodeCreationForm.view";
import { FailureDetailsSettingsContext } from "contexts/FailureDetailsSettings/FailureDetailsSettingsContext";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useRolesPermissions from "hooks/useRolesPermissions";
import { DefaultTranslationFieldsInTable, FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { translateDefaultEntity } from "utilities/localesUtilities";

export const FailureDetailsSettingsList = ({ title, listedItems, corporateId }: IFailureDetailsSettingsList) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { machineCenterId } = useParams();
    const themeStyles = useTheme();
    const [openFailureTypesModal, setOpenFailureTypesModal] = useState<boolean>(false);
    const [openFailureCodeModal, setOpenFailureCodeModal] = useState<boolean>(false);
    const [openSecondaryFailureCodeModal, setOpenSecondaryFailureCodeModal] = useState<boolean>(false);
    const {
        selectedFailureCodeId,
        handleSelectedFailureCodeId,
        handleSelectedFailureTypeId,
        selectedFunctionalLocationId,
        isSelectedFailureTypeLocationSpecific,
        selectedFailureTypeId,
        handleIsSelectedFailureTypeLocationSpecific,
    } = useContext(FailureDetailsSettingsContext);

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const { failureTypes, refetch: failureTypesRefetch } = FailureDetailsEntity.GetFailureTypes();
    const { addLocationSpecificFunctionalLocationFailureCodeRelation } =
        FailureDetailsEntity.InsertLocationSpecificFunctionalLocationFailureCodeRelation();
    const { removeLocationSpecificFunctionalLocationFailureCodeRelation } =
        FailureDetailsEntity.DeleteLocationSpecificFunctionalLocationFailureCodeRelation();
    const { addNonLocationSpecificMachineCenterFailureCodeRelation } =
        FailureDetailsEntity.InsertNonLocationSpecificMachineCenterFailureCodeRelation();
    const { removeNonLocationSpecificMachineCenterFailureCodeRelation } =
        FailureDetailsEntity.DeleteNonLocationSpecificMachineCenterFailureCodeRelation();
    const { locationSpecificFailureCodesIds, refetch: locationSpecificFailureCodesIdsRefetch } =
        FailureDetailsEntity.GetLocationSpecificFailureCodesByFunctionalLocationId(selectedFunctionalLocationId!);
    const { nonLocationSpecificFailureCodesIds, refetch: nonLocationSpecificFailureCodesIdsRefetch } =
        FailureDetailsEntity.GetNonLocationSpecificFailureCodesByMachineCenterId(+machineCenterId!);
    const [selectAllSecondaryFailureCode, setSelectAllSecondaryFailureCode] = useState<boolean>(false);

    // handle selected failure type logic
    const handleSelectedFailureType = useCallback(
        (value: IFailureType) => (event: React.MouseEvent<HTMLDivElement>) => {
            handleSelectedFailureTypeId(value.failure_type_id);
            handleIsSelectedFailureTypeLocationSpecific(value.is_location_specific);
        },
        [handleSelectedFailureTypeId, handleIsSelectedFailureTypeLocationSpecific],
    );

    // handle selected primary failure code logic
    const handleSelectedFailureCode = useCallback(
        (value: number) => (event: React.MouseEvent<HTMLDivElement>) => {
            handleSelectedFailureCodeId(value);
        },
        [handleSelectedFailureCodeId],
    );

    //selected failure type from the failure types list
    const selectedFailureType = useMemo(
        () => failureTypes.filter((failureType: IFailureType) => failureType.failure_type_id === selectedFailureTypeId),
        [failureTypes, selectedFailureTypeId],
    );

    // list of default failure types
    const defaultFailureTypes = failureTypes
        ?.filter(
            (failureType: IFailureType) =>
                failureType.corporate_id === null &&
                failureType.code !== FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED &&
                failureType.code !== FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER,
        )
        .sort((a, b) => a.name.localeCompare(b.name));

    // list of custom failure types
    const customFailureTypes = failureTypes
        ?.filter((failureType: IFailureType) => failureType.corporate_id !== null)
        .sort((a, b) => a.name.localeCompare(b.name));

    // this function checks if the parent failure type of any failure code is location specific or not
    const isFailureCodeLocationSpecific = useCallback(
        (failureCodeId: number) => {
            const defaultFailureType = defaultFailureTypes.find((defaultFailureType) =>
                defaultFailureType.failure_codes.find((failureCode) => failureCode.failure_code_id === failureCodeId),
            )?.is_location_specific;

            const customFailureType = customFailureTypes.find((customFailureType) =>
                customFailureType.failure_codes.find((failureCode) => failureCode.failure_code_id === failureCodeId),
            )?.is_location_specific;
            return defaultFailureType ? defaultFailureType : customFailureType;
        },
        [defaultFailureTypes, customFailureTypes],
    );

    // primary failure codes associated with the selected failure code
    const failureCodesByFailureTypeId = useMemo(() => {
        const failureCodes = selectedFailureType[0]?.failure_codes.filter(
            (failureCode: IFailureCode) => failureCode.primary_code_id === null,
        );

        return failureCodes
            ?.map((failureCode: IFailureCode) => {
                return {
                    ...failureCode,
                    failure_type_location_specific: selectedFailureType[0]?.is_location_specific,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedFailureType]);

    // secondary failure codes associated with the selected failure code
    const secondaryFailureCodes = useMemo(() => {
        const failureCodes = selectedFailureType[0]?.failure_codes.filter(
            (failureCode: IFailureCode) =>
                failureCode.primary_code_id && failureCode.primary_code_id === selectedFailureCodeId,
        );

        return failureCodes
            ?.map((failureCode: IFailureCode) => {
                return {
                    ...failureCode,
                    failure_type_location_specific: selectedFailureType[0]?.is_location_specific,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedFailureCodeId, selectedFailureType]);

    // list of location specific failure codes by each functional location they are associated with
    const locationSpecificFailureCodesByFunctionalLocation: number[] = locationSpecificFailureCodesIds.map(
        (item) => item.failure_code_id,
    );

    // list of non-location specific failure codes by each machine center they are associated with
    const nonLocationSpecificFaiureCodesByMachineCenter: number[] = nonLocationSpecificFailureCodesIds.map(
        (item) => item.failure_code_id,
    );

    // modals logic
    const handleOpenFailureTypesModal = () => setOpenFailureTypesModal(true);

    const handleOpenFailureCodeModal = () => {
        handleSelectedFailureCodeId(null);
        setOpenFailureCodeModal(true);
    };

    const handleOpenSecondaryFailureCodeModal = () => {
        setOpenSecondaryFailureCodeModal(true);
    };

    const handleCloseFailureTypesModal = useCallback(() => {
        failureTypesRefetch();
        setOpenFailureTypesModal(false);
    }, [setOpenFailureTypesModal, failureTypesRefetch]);

    const handleCloseFailureCodesModal = useCallback(() => {
        failureTypesRefetch();
        setOpenFailureCodeModal(false);
    }, [setOpenFailureCodeModal, failureTypesRefetch]);

    const handleCloseSecondaryFailureCodesModal = useCallback(() => {
        failureTypesRefetch();
        setOpenSecondaryFailureCodeModal(false);
    }, [setOpenSecondaryFailureCodeModal, failureTypesRefetch]);

    // failure types and failure codes creation, and relation logic
    const handleAddLocationSpecificFunctionalLocationFailureCodeRelation = useCallback(
        async (checkedFailureCodesIds: number[]) => {
            const locationSpecificFunctionalLocationFailureCode = checkedFailureCodesIds.map(
                (checkedFailureCodeId: number) => {
                    const rowInfo = {
                        functional_location_id: selectedFunctionalLocationId,
                        failure_code_id: checkedFailureCodeId,
                    };

                    return rowInfo;
                },
            );

            try {
                const response = await addLocationSpecificFunctionalLocationFailureCodeRelation({
                    variables: {
                        location_specific_functional_location_failure_code:
                            locationSpecificFunctionalLocationFailureCode,
                    },
                });

                if (response) {
                    locationSpecificFailureCodesIdsRefetch!();
                }
            } catch {
                return;
            }
        },
        [
            addLocationSpecificFunctionalLocationFailureCodeRelation,
            selectedFunctionalLocationId,
            locationSpecificFailureCodesIdsRefetch,
        ],
    );

    const handleRemoveLocationSpecificFunctionalLocationFailureCodeRelation = useCallback(
        async (checkedFailureCodeIds: number[]) => {
            try {
                const response = await removeLocationSpecificFunctionalLocationFailureCodeRelation({
                    variables: {
                        functional_location_id: selectedFunctionalLocationId,
                        failure_code_id: checkedFailureCodeIds,
                    },
                });

                if (response) {
                    locationSpecificFailureCodesIdsRefetch!();
                }
            } catch {
                return;
            }
        },
        [
            removeLocationSpecificFunctionalLocationFailureCodeRelation,
            selectedFunctionalLocationId,
            locationSpecificFailureCodesIdsRefetch,
        ],
    );

    const handleAddNonLocationSpecificMachineCenterFailureCodeRelation = useCallback(
        async (checkedFailureCodesIds: number[]) => {
            const nonLocationSpecificfunctionalLocationFailureCodeValues = checkedFailureCodesIds.map(
                (checkedFailureCodeId: number) => {
                    const rowInfo = {
                        machine_center_id: +machineCenterId!,
                        failure_code_id: checkedFailureCodeId,
                    };

                    return rowInfo;
                },
            );

            try {
                const response = await addNonLocationSpecificMachineCenterFailureCodeRelation({
                    variables: {
                        non_location_specific_machine_center_failure_code:
                            nonLocationSpecificfunctionalLocationFailureCodeValues,
                    },
                });

                if (response) {
                    nonLocationSpecificFailureCodesIdsRefetch!();
                }
            } catch {
                return;
            }
        },
        [
            addNonLocationSpecificMachineCenterFailureCodeRelation,
            machineCenterId,
            nonLocationSpecificFailureCodesIdsRefetch,
        ],
    );

    const handleRemoveNonLocationSpecificMachineCenterFailureCodeRelation = useCallback(
        async (checkedFailureCodeIds: number[]) => {
            try {
                const response = await removeNonLocationSpecificMachineCenterFailureCodeRelation({
                    variables: {
                        machine_center_id: +machineCenterId!,
                        failure_code_id: checkedFailureCodeIds,
                    },
                });

                if (response) {
                    nonLocationSpecificFailureCodesIdsRefetch!();
                }
            } catch {
                return;
            }
        },
        [
            removeNonLocationSpecificMachineCenterFailureCodeRelation,
            machineCenterId,
            nonLocationSpecificFailureCodesIdsRefetch,
        ],
    );

    //the two following functions check or uncheck all children failure codes when a parent failure code is checked or unchecked
    const handleChildrenFailureCodesCheck = useCallback(
        (failureCodeId: number) => {
            const filteredFailureCodes = (selectedFailureType[0]?.failure_codes || [])
                .filter((failureCode) =>
                    isSelectedFailureTypeLocationSpecific
                        ? !locationSpecificFailureCodesIds.some(
                              (locationSpecificFailureCode) =>
                                  failureCode.failure_code_id === locationSpecificFailureCode.failure_code_id,
                          )
                        : !nonLocationSpecificFailureCodesIds.some(
                              (nonLocationSpecifiFailureCode) =>
                                  failureCode.failure_code_id === nonLocationSpecifiFailureCode.failure_code_id,
                          ),
                )
                .reduce((acc: number[], failureCode) => {
                    if (failureCode.primary_code_id && failureCode.primary_code_id === failureCodeId) {
                        acc.push(failureCode.failure_code_id);
                    }
                    return acc;
                }, []);

            const isItLocationSpecific = isFailureCodeLocationSpecific(filteredFailureCodes[0]);

            if (isSelectedFailureTypeLocationSpecific && selectedFunctionalLocationId && isItLocationSpecific) {
                handleAddLocationSpecificFunctionalLocationFailureCodeRelation(filteredFailureCodes);
            } else if (machineCenterId && !isSelectedFailureTypeLocationSpecific && !isItLocationSpecific) {
                handleAddNonLocationSpecificMachineCenterFailureCodeRelation(filteredFailureCodes);
            }
        },
        [
            handleAddLocationSpecificFunctionalLocationFailureCodeRelation,
            handleAddNonLocationSpecificMachineCenterFailureCodeRelation,
            isSelectedFailureTypeLocationSpecific,
            selectedFunctionalLocationId,
            selectedFailureType,
            locationSpecificFailureCodesIds,
            nonLocationSpecificFailureCodesIds,
            machineCenterId,
            isFailureCodeLocationSpecific,
        ],
    );

    const handleChildrenFailureCodesUncheck = useCallback(
        (failureCodeId: number) => {
            const filteredFailureCodeIds = (selectedFailureType[0]?.failure_codes || []).reduce(
                (acc: number[], failureCode) => {
                    if (failureCode.primary_code_id && failureCode.primary_code_id === failureCodeId) {
                        acc.push(failureCode.failure_code_id);
                    }
                    return acc;
                },
                [],
            );

            const isItLocationSpecific = isFailureCodeLocationSpecific(filteredFailureCodeIds[0]);

            if (isSelectedFailureTypeLocationSpecific && selectedFunctionalLocationId && isItLocationSpecific) {
                handleRemoveLocationSpecificFunctionalLocationFailureCodeRelation(filteredFailureCodeIds);
            } else if (machineCenterId && !isItLocationSpecific) {
                handleRemoveNonLocationSpecificMachineCenterFailureCodeRelation(filteredFailureCodeIds);
            }
        },
        [
            handleRemoveLocationSpecificFunctionalLocationFailureCodeRelation,
            handleRemoveNonLocationSpecificMachineCenterFailureCodeRelation,
            isSelectedFailureTypeLocationSpecific,
            selectedFailureType,
            isFailureCodeLocationSpecific,
            machineCenterId,
            selectedFunctionalLocationId,
        ],
    );

    const handleFailureCodesCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isItLocationSpecific = isFailureCodeLocationSpecific(+event.target.id);

        if (event.target.checked) {
            if (isSelectedFailureTypeLocationSpecific && selectedFunctionalLocationId && isItLocationSpecific) {
                handleAddLocationSpecificFunctionalLocationFailureCodeRelation([+event.target.id]);
            } else if (!isSelectedFailureTypeLocationSpecific && machineCenterId && !isItLocationSpecific) {
                handleAddNonLocationSpecificMachineCenterFailureCodeRelation([+event.target.id]);
            }
            handleSelectedFailureCode(+event.target.id);
            handleChildrenFailureCodesCheck(+event.target.id);
        } else {
            if (isSelectedFailureTypeLocationSpecific && isItLocationSpecific) {
                handleRemoveLocationSpecificFunctionalLocationFailureCodeRelation([+event.target.id]);
            } else if (!isSelectedFailureTypeLocationSpecific && machineCenterId && !isItLocationSpecific) {
                handleRemoveNonLocationSpecificMachineCenterFailureCodeRelation([+event.target.id]);
            }
            handleChildrenFailureCodesUncheck(+event.target.id);
        }
    };

    const handleSecondaryFailureCodesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isItLocationSpecific = isFailureCodeLocationSpecific(+event.target.id);

        if (event.target.checked) {
            if (isSelectedFailureTypeLocationSpecific && selectedFunctionalLocationId && isItLocationSpecific) {
                handleAddLocationSpecificFunctionalLocationFailureCodeRelation([+event.target.id]);
                handleAddLocationSpecificFunctionalLocationFailureCodeRelation([selectedFailureCodeId!]);
            } else if (!isSelectedFailureTypeLocationSpecific && machineCenterId && !isItLocationSpecific) {
                handleAddNonLocationSpecificMachineCenterFailureCodeRelation([+event.target.id]);
                handleAddNonLocationSpecificMachineCenterFailureCodeRelation([selectedFailureCodeId!]);
            }
        } else {
            if (isSelectedFailureTypeLocationSpecific && isItLocationSpecific) {
                handleRemoveLocationSpecificFunctionalLocationFailureCodeRelation([+event.target.id]);
            } else if (!isSelectedFailureTypeLocationSpecific && machineCenterId && !isItLocationSpecific) {
                handleRemoveNonLocationSpecificMachineCenterFailureCodeRelation([+event.target.id]);
            }
        }
    };

    //these two variables handle the behavior for the Select all checkbox in the Secondary failure codes list
    const allNonLocationSpecificSecondaryFailureCodesChecked: boolean = useMemo(() => {
        return secondaryFailureCodes?.every((secondaryCode) => {
            return nonLocationSpecificFailureCodesIds.some((nonLocationSpecificFailureCode) => {
                return nonLocationSpecificFailureCode.failure_code_id === secondaryCode.failure_code_id;
            });
        });
    }, [nonLocationSpecificFailureCodesIds, secondaryFailureCodes]);

    const allLocationSpecificSecondaryFailureCodesChecked: boolean = useMemo(() => {
        return secondaryFailureCodes?.every((secondaryCode) => {
            return locationSpecificFailureCodesIds.some((locationSpecificFailureCode) => {
                return locationSpecificFailureCode.failure_code_id === secondaryCode.failure_code_id;
            });
        });
    }, [locationSpecificFailureCodesIds, secondaryFailureCodes]);

    useEffect(() => {
        isSelectedFailureTypeLocationSpecific
            ? setSelectAllSecondaryFailureCode(allLocationSpecificSecondaryFailureCodesChecked)
            : setSelectAllSecondaryFailureCode(allNonLocationSpecificSecondaryFailureCodesChecked);
    }, [
        allLocationSpecificSecondaryFailureCodesChecked,
        allNonLocationSpecificSecondaryFailureCodesChecked,
        isSelectedFailureTypeLocationSpecific,
    ]);

    //handle Select All check for secondary failure codes
    const handleSelectAllSecondaryFailureCodes = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isItLocationSpecific = isFailureCodeLocationSpecific(selectedFailureCodeId!);

        setSelectAllSecondaryFailureCode(event.target.checked);
        if (event.target.checked) {
            handleChildrenFailureCodesCheck(selectedFailureCodeId!);
            if (isSelectedFailureTypeLocationSpecific && isItLocationSpecific) {
                handleAddLocationSpecificFunctionalLocationFailureCodeRelation([selectedFailureCodeId!]);
            } else if (!isSelectedFailureTypeLocationSpecific && machineCenterId && !isItLocationSpecific) {
                handleAddNonLocationSpecificMachineCenterFailureCodeRelation([selectedFailureCodeId!]);
            }
        } else {
            handleChildrenFailureCodesUncheck(selectedFailureCodeId!);
        }
    };

    // get total amount of primary failure codes checked by failure type
    const getPrimaryFailureCodesAmount = useCallback(
        (failureType: IFailureType) => {
            const primaryFailureCodes: IFailureCode[] = failureType.failure_codes.filter(
                (failureCode: IFailureCode) => failureCode.primary_code_id === null,
            );

            const filteredFailureCodes = failureType.is_location_specific
                ? primaryFailureCodes.filter((failureCode: IFailureCode) =>
                      locationSpecificFailureCodesByFunctionalLocation?.includes(failureCode.failure_code_id),
                  )
                : primaryFailureCodes.filter((failureCode: IFailureCode) =>
                      nonLocationSpecificFaiureCodesByMachineCenter?.includes(failureCode.failure_code_id),
                  );

            return filteredFailureCodes.length;
        },
        [locationSpecificFailureCodesByFunctionalLocation, nonLocationSpecificFaiureCodesByMachineCenter],
    );

    return (
        <Box className={classes.container}>
            {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters ? (
                <button
                    className={classes.button}
                    onClick={
                        listedItems === "failureTypes"
                            ? handleOpenFailureTypesModal
                            : listedItems === "failureCodes"
                            ? handleOpenFailureCodeModal
                            : handleOpenSecondaryFailureCodeModal
                    }
                    disabled={
                        (listedItems === "failureCodes" && !selectedFailureTypeId) ||
                        (listedItems === "secondaryFailureCodes" && !selectedFailureCodeId)
                    }
                    style={{
                        opacity:
                            (listedItems === "failureCodes" && !selectedFailureTypeId) ||
                            (listedItems === "secondaryFailureCodes" && !selectedFailureCodeId)
                                ? "0.2"
                                : "1",
                    }}
                >
                    <Box className={classes.titleContainer}>
                        <Typography variant="subtitle2">{title.toUpperCase()}</Typography>
                        <AddCircleOutlinedIcon className={classes.icon} />
                    </Box>
                </button>
            ) : (
                <Box className={classes.titleContainer}>
                    <Typography variant="subtitle2">{title.toUpperCase()}</Typography>
                </Box>
            )}
            {listedItems === "failureTypes" && (
                <List className={classes.list}>
                    {defaultFailureTypes && defaultFailureTypes.length > 0 && (
                        <>
                            <Box className={classes.failureTypesCategoryTitleBox}>
                                <Typography className={classes.failureTypesCategoryTitle} variant="subtitle1">
                                    {t("failure_details_setup.default")}
                                </Typography>
                                <Tooltip title={t("failure_details_setup.default_info")} placement="right">
                                    <InfoOutlinedIcon className={classes.infoIcon} />
                                </Tooltip>
                            </Box>
                            {defaultFailureTypes.map((failureType: IFailureType) => (
                                <Box key={failureType.failure_type_id}>
                                    {!failureType.corporate_id && (
                                        <ListItemButton
                                            onClick={handleSelectedFailureType(failureType)}
                                            className={classes.listItemButton}
                                            sx={{
                                                background:
                                                    selectedFailureTypeId === failureType.failure_type_id
                                                        ? "#DEF3F3"
                                                        : "unset",
                                            }}
                                        >
                                            <ListItemText
                                                classes={{ primary: classes.item }}
                                                primary={
                                                    translateDefaultEntity(
                                                        i18n.language,
                                                        failureType,
                                                        "name",
                                                        DefaultTranslationFieldsInTable.FAILURE_TYPE,
                                                    ) as string
                                                }
                                            />
                                            <span
                                                className={classes.colorCode}
                                                style={{
                                                    background:
                                                        getPrimaryFailureCodesAmount(failureType) > 0
                                                            ? failureType.color_code
                                                            : themeStyles.palette.corporateBO.checkbox,
                                                }}
                                            >
                                                <Typography sx={{ fontSize: "12px !important" }} variant="subtitle1">
                                                    {getPrimaryFailureCodesAmount(failureType)}
                                                </Typography>
                                            </span>
                                        </ListItemButton>
                                    )}
                                </Box>
                            ))}
                        </>
                    )}
                    {customFailureTypes && customFailureTypes.length > 0 && (
                        <>
                            <Box className={classes.failureTypesCategoryTitleBox}>
                                <Typography className={classes.failureTypesCategoryTitle} variant="subtitle1">
                                    {t("failure_details_setup.custom")}
                                </Typography>
                                <Tooltip title={t("failure_details_setup.custom_info")} placement="right">
                                    <InfoOutlinedIcon className={classes.infoIcon} />
                                </Tooltip>
                            </Box>
                            {customFailureTypes.map((failureType: IFailureType) => (
                                <ListItemButton
                                    onClick={handleSelectedFailureType(failureType)}
                                    key={failureType.failure_type_id}
                                    className={classes.listItemButton}
                                    sx={{
                                        background:
                                            selectedFailureTypeId === failureType.failure_type_id ? "#DEF3F3" : "unset",
                                    }}
                                >
                                    <ListItemText
                                        classes={{ primary: classes.item }}
                                        primary={failureType.name}
                                        key={failureType.failure_type_id}
                                    />
                                    <span
                                        className={classes.colorCode}
                                        style={{
                                            background:
                                                getPrimaryFailureCodesAmount(failureType) > 0
                                                    ? failureType.color_code
                                                    : themeStyles.palette.corporateBO.checkbox,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "12px !important" }} variant="subtitle1">
                                            {getPrimaryFailureCodesAmount(failureType)}
                                        </Typography>
                                    </span>
                                </ListItemButton>
                            ))}
                        </>
                    )}
                </List>
            )}
            {listedItems === "failureCodes" &&
                failureCodesByFailureTypeId &&
                failureCodesByFailureTypeId.length > 0 && (
                    <List className={classes.list}>
                        {failureCodesByFailureTypeId.map((failureCode: IFailureCode) => (
                            <ListItemButton
                                onClick={handleSelectedFailureCode(failureCode.failure_code_id)}
                                key={failureCode.failure_code_id}
                                className={classes.listItemButton}
                                sx={{
                                    background:
                                        selectedFailureCodeId === failureCode.failure_code_id ? "#DEF3F3" : "unset",
                                }}
                            >
                                {(((failureCode.failure_type_location_specific || failureCode.corporate_id) &&
                                    selectedFunctionalLocationId) ||
                                    (!failureCode.failure_type_location_specific &&
                                        failureCode.corporate_id &&
                                        machineCenterId)) && (
                                    <Checkbox
                                        disabled={!CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters}
                                        checked={
                                            locationSpecificFailureCodesByFunctionalLocation?.includes(
                                                failureCode.failure_code_id,
                                            ) ||
                                            nonLocationSpecificFaiureCodesByMachineCenter?.includes(
                                                failureCode.failure_code_id,
                                            )
                                        }
                                        onChange={handleFailureCodesCheckChange}
                                        inputProps={{ "aria-label": "controlled" }}
                                        value={failureCode.name}
                                        className={classes.checkbox}
                                        id={failureCode.failure_code_id.toString()}
                                    />
                                )}
                                <ListItemText
                                    classes={{ primary: classes.item }}
                                    primary={translateDefaultEntity(
                                        i18n.language,
                                        failureCode,
                                        "name",
                                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                                    )}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            {listedItems === "secondaryFailureCodes" && secondaryFailureCodes && secondaryFailureCodes.length > 0 && (
                <>
                    <ListItemButton className={classes.listItemButton}>
                        <Box className={classes.failureTypesCategoryTitleBox}>
                            {((selectedFunctionalLocationId &&
                                secondaryFailureCodes[0].failure_type_location_specific &&
                                selectedFunctionalLocationId) ||
                                (!secondaryFailureCodes[0].failure_type_location_specific && machineCenterId)) && (
                                <Checkbox
                                    disabled={!CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters}
                                    checked={selectAllSecondaryFailureCode}
                                    onChange={handleSelectAllSecondaryFailureCodes}
                                    inputProps={{ "aria-label": "controlled" }}
                                    value={selectAllSecondaryFailureCode}
                                    className={classes.checkbox}
                                    id={"select_all"}
                                />
                            )}
                            <Typography className={classes.selectAllOption} variant="subtitle1">
                                {t("failure_details_setup.select_all")}
                            </Typography>
                        </Box>
                    </ListItemButton>
                    <List className={classes.list}>
                        {secondaryFailureCodes.map((secondaryFailureCode: IFailureCode) => (
                            <ListItemButton
                                key={secondaryFailureCode.failure_code_id}
                                className={classes.listItemButton}
                            >
                                {(((secondaryFailureCode.failure_type_location_specific ||
                                    secondaryFailureCode.corporate_id) &&
                                    selectedFunctionalLocationId) ||
                                    (!secondaryFailureCode.failure_type_location_specific &&
                                        secondaryFailureCode.corporate_id &&
                                        machineCenterId)) && (
                                    <Checkbox
                                        disabled={!CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters}
                                        checked={
                                            locationSpecificFailureCodesByFunctionalLocation?.includes(
                                                secondaryFailureCode.failure_code_id,
                                            ) ||
                                            nonLocationSpecificFaiureCodesByMachineCenter?.includes(
                                                secondaryFailureCode.failure_code_id,
                                            )
                                        }
                                        onChange={handleSecondaryFailureCodesChange}
                                        inputProps={{ "aria-label": "controlled" }}
                                        value={secondaryFailureCode.name}
                                        className={classes.checkbox}
                                        id={secondaryFailureCode.failure_code_id.toString()}
                                    />
                                )}
                                <ListItemText
                                    classes={{ primary: classes.item }}
                                    primary={
                                        translateDefaultEntity(
                                            i18n.language,
                                            secondaryFailureCode,
                                            "name",
                                            DefaultTranslationFieldsInTable.FAILURE_CODE,
                                        ) as string
                                    }
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </>
            )}

            <AddFailureTypesModal
                open={openFailureTypesModal}
                handleClose={handleCloseFailureTypesModal}
                title={t("failure_details_setup.add_failure_types_modal.title")}
            >
                <FailureTypeCreationForm handleClose={handleCloseFailureTypesModal} corporateId={corporateId} />
            </AddFailureTypesModal>
            <AddFailureCodesModal
                open={openFailureCodeModal}
                handleClose={handleCloseFailureCodesModal}
                title={t("failure_details_setup.add_failure_codes_modal.title")}
            >
                <FailureCodeCreationForm handleClose={handleCloseFailureCodesModal} corporateId={corporateId} />
            </AddFailureCodesModal>
            <AddFailureCodesModal
                open={openSecondaryFailureCodeModal}
                handleClose={handleCloseSecondaryFailureCodesModal}
                title={t("failure_details_setup.add_secondary_failure_codes_modal.title")}
            >
                <FailureCodeCreationForm
                    handleClose={handleCloseSecondaryFailureCodesModal}
                    corporateId={corporateId}
                />
            </AddFailureCodesModal>
        </Box>
    );
};
